import { FunctionComponent, useEffect, useState } from 'react'
import styles from './profileAddress.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import EditIcon from '@mui/icons-material/Edit'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import CitySelect from '@/components/_custom/CitySelect/citySelect'
import EditAddressDialog from './Dialog/editAddressDialog'

interface ParentAddressProps {
    signUp?: boolean
    parent?: boolean
    country: string
    setCountry: (country: string) => void
    address: string
    setAddress: (address: string) => void
    city: string
    setCity: (city: string) => void
    zip: string
    setZip: (zip: string) => void
}

const ProfileAddress: FunctionComponent<ParentAddressProps> = ({
    signUp,
    parent,
    country,
    setCountry,
    address,
    setAddress,
    city,
    setCity,
    zip,
    setZip
}: ParentAddressProps) => {
    const { t } = useTranslation()

    const [startSignUp, setStartSignUp] = useState<boolean>(false)

    useEffect(() => {
        const shouldStartSignUp: boolean = signUp || country === ''
        setStartSignUp(shouldStartSignUp)
    }, [signUp, country])

    const [countrySelected, setCountrySelected] = useState<boolean>(country !== '')
    const [citySelected, setCitySelected] = useState<boolean>(false)

    const [selectCityManually, setSelectCityManually] = useState<boolean>(false)

    const [editAddressDialogOpen, setEditAddressDialogOpen] = useState<boolean>(false)

    function handleSetCountry(selectedCountry: string): void {
        setCountry(selectedCountry)
        if (selectedCountry !== '') {
            setCountrySelected(true)
        } else {
            setCountrySelected(false)
        }
    }

    useEffect(() => {
        if (city !== '' && zip !== '') {
            setCitySelected(true)
        } else {
            setCitySelected(false)
        }
    }, [city, zip])

    useEffect(() => {
        if (country === '') {
            setCountrySelected(false)
        }
    }, [country])

    return startSignUp ? (
        <>
            <div className={styles.Input}>
                <div className={styles.UserInput}>
                    <CountrySelect country={country} setCountry={handleSetCountry} parent={parent} required />
                </div>
            </div>
            {((countrySelected && !citySelected) || citySelected) && (
                <>
                    <div className={styles.Input}>
                        {selectCityManually ? (
                            <UserInput
                                label={t('city')}
                                value={city}
                                setValue={setCity}
                                type='text'
                                required
                                parent={parent}
                                fullWidth
                                className={styles.UserInput}
                                format={'pascal'}
                                unallowedCharacters={'num'}
                            />
                        ) : (
                            <div className={styles.UserInput}>
                                <CitySelect
                                    key={country}
                                    city={city}
                                    setCity={setCity}
                                    parent={parent}
                                    country={country}
                                />
                            </div>
                        )}
                        <UserInput
                            label={t('zipCode')}
                            value={zip}
                            setValue={setZip}
                            type='text'
                            required
                            parent={parent}
                            fullWidth
                            className={styles.UserInput}
                            format={'zipCode'}
                            unallowedCharacters={'alpha'}
                        />
                    </div>
                    {!citySelected && (
                        <div className={styles.TextContainer}>
                            <a className={styles.Text} onClick={() => setSelectCityManually(true)}>
                                {t('myCityWasNotInList')}
                            </a>
                        </div>
                    )}
                </>
            )}
            {countrySelected && citySelected && (
                <div className={styles.Input}>
                    <UserInput
                        label={t('address')}
                        value={address}
                        setValue={setAddress}
                        type='text'
                        required
                        parent={parent}
                        fullWidth
                        className={styles.UserInput}
                        unallowedCharacters={'special'}
                    />
                </div>
            )}
        </>
    ) : (
        <>
            <div className={styles.Input}>
                <UserInput
                    label={t('address')}
                    value={address}
                    type='text'
                    required
                    parent={parent}
                    fullWidth
                    className={styles.UserInput}
                    unallowedCharacters={'special'}
                    disabled
                />
                <UserInput
                    label={t('city')}
                    value={city}
                    type='text'
                    required
                    parent={parent}
                    fullWidth
                    className={styles.UserInput}
                    format={'pascal'}
                    unallowedCharacters={'num'}
                    disabled
                />
            </div>
            <div className={styles.Input}>
                <UserInput
                    label={t('zipCode')}
                    value={zip}
                    setValue={setZip}
                    type='text'
                    required
                    parent={parent}
                    fullWidth
                    className={styles.UserInput}
                    format={'zipCode'}
                    unallowedCharacters={'alpha'}
                    disabled
                />
                <div className={styles.UserInput}>
                    <CountrySelect country={country} parent={parent} disabled required />
                </div>
            </div>
            <div className={styles.ButtonContainer}>
                <ButtonTextIcon
                    type={'custom'}
                    customText={t('editMyAddress')}
                    customIcon={<EditIcon />}
                    parent={parent}
                    className={styles.Button}
                    onClick={() => setEditAddressDialogOpen(true)}
                />
            </div>
            {editAddressDialogOpen && (
                <EditAddressDialog
                    open={editAddressDialogOpen}
                    handleClose={() => setEditAddressDialogOpen(false)}
                    parent={parent}
                    country={country}
                    handleSetCountry={handleSetCountry}
                    city={city}
                    setCity={setCity}
                    zip={zip}
                    setZip={setZip}
                    address={address}
                    setAddress={setAddress}
                />
            )}
        </>
    )
}

export default ProfileAddress
